import { useEffect, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { PageHeader, PageShell } from '@/components/PageHeader';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trophy, Crown } from 'lucide-react';

export default function Leaderboard() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7) + '-01';
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get('/analytics/leaderboard', { params: { from_date: from, to_date: to } });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [from, to]);

  const max = Math.max(1, ...rows.map(r => r.joinings));

  return (
    <PageShell>
      <PageHeader
        eyebrow={`${from} → ${to}`}
        title="Performance leaderboard"
        subtitle="Ranked by joinings (candidates successfully joined the client). Offers, lineups & calls shown alongside — the top spot gets the crown."
        actions={
          <div className="flex items-center gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">From</Label>
              <Input data-testid="lb-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">To</Label>
              <Input data-testid="lb-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 h-9" />
            </div>
          </div>
        }
      />

      <div className="space-y-3" data-testid="leaderboard-list">
        {rows.map((r, i) => (
          <div key={r.user_id}
            className={`tactical-card p-5 flex items-center gap-6 hover:-translate-y-0.5 transition-transform ${i === 0 ? 'border-primary bg-gradient-to-r from-primary/10 to-transparent' : ''}`}>
            <div className={`font-display font-black text-5xl w-16 ${i === 0 ? 'text-primary' : i < 3 ? 'text-foreground' : 'text-muted-foreground'}`}>
              {String(i + 1).padStart(2, '0')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-display text-xl font-black tracking-tight truncate">{r.user_name}</div>
                {i === 0 && <Crown className="w-5 h-5 text-primary" />}
              </div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${(r.joinings / max) * 100}%` }} />
              </div>
              <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-x-6">
                <span>{r.offers_rolled} offers</span>
                <span>{r.lineups} lineups</span>
                <span>{r.interviews_processed} interviews</span>
                <span>{r.calls_made} calls</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-4xl font-black tracking-tight text-emerald-500">{r.joinings}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Joinings</div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="tactical-card p-12 text-center text-muted-foreground">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-50" />
            No data for this range yet.
          </div>
        )}
      </div>
    </PageShell>
  );
}
